// Import necessary hooks and libraries
import {createContext, ReactNode, use, useCallback, useEffect, useState} from "react";
import useWebSocket, {ReadyState} from "react-use-websocket";
import {WSS_URL} from "@/lib/constants";
import {useSession} from "@/lib/auth/auth";
import {chat} from "@/lib/proto";
import {v7 as uuidv7} from "uuid";
import {useImmerReducer} from "use-immer";
import {enableMapSet} from "immer";
import Chat = chat.Chat;
import Request = chat.Request;
import ChatResponse = chat.Response;
import ChatMessage = chat.ChatMessage;
import LoginRequest = chat.LoginRequest;
import ListChatsRequest = chat.ListChatsRequest;
import ReplayRequest = chat.ReplayRequest;
import SendChatMessageRequest = chat.SendChatMessageRequest;
import Avatar = chat.Avatar;
import MessageReadRequest = chat.MessageReadRequest;
import {useGlobalSearchParams} from "expo-router";

// Create a context for chat messages
const ChatContext = createContext<ChatProviderContextProps>({
    chats: new Map(),
    chatConnected: false,
    chatBusy: false,
    currentChat: undefined,
    chatError: null,
    unreadChatMessages: undefined,
    unreadMessagesForChat: () => null,
    sendChatMessage: () => null,
    subscribeToChat: () => null,
    markMessageAsRead: () => null,
});

type ChatProviderProps = {
    children?: ReactNode,
}

type ChatProviderContextProps = {
    chats: Map<string, ChatRecord>,
    chatConnected: boolean,
    chatBusy: boolean,
    currentChat: ChatRecord | undefined,
    unreadChatMessages: number | undefined,
    unreadMessagesForChat: (chat_id: string) => any,
    chatError: Error | null,
    sendChatMessage: (message: SendChatMessageRequest, callback?: () => any) => any,
    subscribeToChat: (id: string) => any,
    markMessageAsRead: (chat_id: string, message_id: string) => any,
}

export type Reactions = Map<string, Avatar[]>;

export interface ChatRecord {
    chat: Chat;
    messages: Map<string, ChatMessage>;
    // maps to message key
    reactions: Map<string, Reactions>;
    pendingMessages: Map<string, SendChatMessageRequest>;
    numUnread: number,
}

// Define a custom hook to access the chat messages context
export function useChat() {
    const value = use(ChatContext);
    if (!value) {
        throw new Error('useChat must be wrapped in a <ChatProvider />');
    }
    return value;
}

type ChatMutation = {
    type: "response",
    msg: ChatResponse,
} | {
    type: "pendingMessage",
    msg: SendChatMessageRequest,
} | {
    type: "markAsRead",
    msg: MessageReadRequest,
};

type MessagesMutation = {
    type: "add" | "remove",
    msg?: ChatResponse,
};

// Define the ChatProvider component to provide chat messages context
export const ChatProvider = ({children}: ChatProviderProps) => {
    const {session} = useSession();
    const [chatConnected, setChatConnected] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [messageReceivedCallbacks, setMessageReceivedCallbacks] = useState<Map<string, () => any>>(new Map());

    const {chat_id} = useGlobalSearchParams();
    const chatID = chat_id?.toString();

    // for Immer
    enableMapSet();

    const [messages, updateMessages] = useImmerReducer((messages: ChatResponse[], message: MessagesMutation) => {
        switch (message.type) {
            case "add":
                messages.push(message.msg!);
                break;
            case "remove":
                messages.shift();
                break;
            default:
        }
    }, []);

    // Initialize the WebSocket connection and retrieve necessary properties
    const {
        sendMessage: sM,
        readyState,
        getWebSocket
    } = useWebSocket(WSS_URL + "/chat/v1", {
        disableJson: true,
        onMessage: (event) => {
            if (!event.data) {
                return;
            }
            new Response(event.data).arrayBuffer().then(buf => {
                const payload = new Uint8Array(buf);
                try {
                    const msg = ChatResponse.decode(payload)
                    if (!msg) {
                        return;
                    }
                    updateMessages({type: "add", msg: msg});
                } catch (err) {
                    console.debug("Unable to decode chat message:", payload, err)
                    return;
                }
            })
        },
        onError: (event) => {
            console.warn("Chat error event: " + JSON.stringify(event));
            setError(new Error("Problemer med forbindelsen til server"))
        },
        shouldReconnect: () => true,
        queryParams: {}
    });

    const sendRequest = useCallback(
        (request: Request) => {
            setBusy(true);
            const payload = Request.encode(request).finish();
            setError(null);
            sM(payload);
            setBusy(false);
        },
        [sM],
    );

    // Configure the websocket for binary data.
    // This is not needed for web, but at least by iOS.
    useEffect(() => {
        const sock = getWebSocket() as WebSocket;
        if (sock === null) {
            return;
        }
        sock.binaryType = "blob";
    }, [getWebSocket]);

    // Handshake when initializing connection
    useEffect(() => {
        if (readyState !== ReadyState.OPEN || !session) {
            return;
        }
        console.info("Connected to chat server");
        sendRequest(Request.create({
            authenticate: LoginRequest.create({
                sessionId: session?.session_id,
            })
        }));
    }, [sendRequest, readyState, session]);

    // Chat state machine and update handling.
    const [chats, updateChat] = useImmerReducer(
        (chats, mutation: ChatMutation) => {
            switch (mutation.type) {
                case "response":
                    const msg = mutation.msg;
                    // console.debug("<- ChatResponse", msg);

                    if (msg.welcome) {
                        setChatConnected(true);
                        sendRequest(Request.create({
                            listChats: ListChatsRequest.create(),
                        }));
                        sendRequest(Request.create({
                            replay: ReplayRequest.create(), // todo: point in time
                        }));

                    } else if (msg.message) {
                        const message = msg.message as ChatMessage;
                        if (!message.chatId || !message.messageId || !message.sender || !message.sender.id) {
                            return;
                        }
                        // console.debug(new Date(), `Got message ${message.messageId} in chat ${message.chatId}`, message);
                        let chat = chats.get(message.chatId);
                        if (chat === undefined) {
                            console.warn(`Ignoring message`, message.messageId, `in unregistered chat`, message.chatId);
                            return;
                        }

                        // round-trip complete!
                        const pendingMessageDeleted = chat.pendingMessages.delete(message.clientMessageId!);

                        if (!!message.reaction && message.reaction.length > 0) {
                            // this is a reaction to another message
                            const inReplyTo = chat.messages.get(message.inReplyTo!);
                            if (inReplyTo === undefined) {
                                return;
                            }
                            const reactionRecords: Reactions = chat.reactions.get(inReplyTo.messageId) || new Map();
                            const reactionList = [...reactionRecords.get(message.reaction) || []];
                            const previousIndex = reactionList.findIndex(avatar => message.sender!.id === avatar.id);
                            // one more of a previous reaction of the same kind will delete it.
                            if (previousIndex !== -1) {
                                reactionList.splice(previousIndex, 1);
                            } else {
                                reactionList.push(message.sender as Avatar);
                            }
                            reactionRecords.set(message.reaction, reactionList);
                            chat.reactions.set(inReplyTo.messageId, reactionRecords);
                            chat.chat.lastActivity = message.receivedAt;
                            // console.log(chat.chat.lastActivity);
                        } else {
                            // this is a message by itself
                            chat.messages.set(message.messageId, message);
                            if (pendingMessageDeleted) {
                                // mark own messages as seen immediately.
                                // this causes bugs due to the message location not being reported.
                                chat.chat.lastReadMessage = message.messageId;
                            } else if (message.messageId > chat.chat.lastReadMessage) {
                                chat.numUnread += 1;
                            }
                            chat.chat.lastActivity = message.receivedAt;
                        }

                        // Insert new chat participant into participant list if not already there
                        if (!!message.sender && !chat.chat.participants.some(i => i.id === message.sender?.id)) {
                            chat.chat.participants.push(message.sender);
                        }

                        // console.debug("Chat", chat.chat.id, "contains", chat.messages.size, "messages");

                        const callback = messageReceivedCallbacks.get(message.clientMessageId);
                        if (callback !== undefined) {
                            // console.debug("Trigger callback for message", message.clientMessageId);
                            messageReceivedCallbacks.delete(message.clientMessageId);
                            setMessageReceivedCallbacks(new Map(messageReceivedCallbacks));
                            callback();
                        }

                    } else if (msg.chat) {
                        // console.debug(msg.chat);
                        if (!msg.chat.id) {
                            return;
                        }
                        // console.debug("Got chat", c.id);
                        const chatRecord: ChatRecord = chats.get(msg.chat.id) || {
                            chat: Chat.create(),
                            messages: new Map(),
                            reactions: new Map(),
                            pendingMessages: new Map(),
                            numUnread: 0,
                        };
                        chatRecord.chat = msg.chat as Chat;
                        chats.set(msg.chat.id, chatRecord);
                    } else if (msg.error) {
                        setError(new Error(msg.error.message!));
                    }
                    break;

                case "pendingMessage":
                    const chat = chats.get(mutation.msg.chatId!);
                    if (chat !== undefined) {
                        chat.pendingMessages.set(mutation.msg.clientMessageId, mutation.msg);
                    }
                    break;

                case "markAsRead":
                    const chatR = chats.get(mutation.msg.chatId);
                    if (chatR === undefined) {
                        return;
                    }
                    if (mutation.msg.messageId <= chatR.chat.lastReadMessage) {
                        // console.debug("Message", mutation.msg.messageId, "is older than", chatR.chat.lastReadMessage);
                        return;
                    }
                    // console.log("Read message", message_id);
                    // console.debug("Mark message", mutation.msg.messageId, "as read");
                    chatR.chat.lastReadMessage = mutation.msg.messageId;

                    // reducer pattern doesn't work here due to some bug
                    chatR.numUnread = 0;
                    chatR.messages.forEach(msg => {
                        if (msg.messageId > chatR.chat.lastReadMessage) {
                            chatR.numUnread += 1;
                        }
                    })

                    break;

                default:
            }
        }, new Map<string, ChatRecord>()
    );

    // Handle the incoming WebSocket messages
    useEffect(() => {
        messages.forEach((msg) => {
            updateChat({type: "response", msg: msg})
            updateMessages({type: "remove"})
        });
    }, [messages, updateChat, updateMessages]);

    const sendChatMessage = useCallback(
        (message: SendChatMessageRequest, callback?: () => any) => {
            if (message.text.length + message.attachments.length + message.reaction.length === 0) {
                return;
            }
            message.clientMessageId = uuidv7();
            updateChat({type: "pendingMessage", msg: message});
            const request = Request.create({
                sendMessage: message,
            });
            if (callback !== undefined) {
                // console.debug("Registering callback for message", message.clientMessageId);
                setMessageReceivedCallbacks(
                    new Map(messageReceivedCallbacks.set(message.clientMessageId, callback))
                );
            }
            return sendRequest(request);
        },
        [updateChat, sendRequest, messageReceivedCallbacks]
    );

    const subscribeToChat = useCallback((chat_id: string) => {
        sendRequest(Request.create({
            replay: ReplayRequest.create({
                chatId: chat_id,
            })
        }))
    }, [sendRequest]);

    const unreadMessagesForChat = (chat_id: string) => {
        const record = chats.get(chat_id);
        if (record === undefined) {
            return 0;
        }
        return record.numUnread;
    };

    const unreadChatMessages = (() => {
        let total = 0;
        chats.forEach(record => total += record.numUnread);
        return total
    })();

    const markMessageAsRead = useCallback((chat_id: string, message_id: string) => {
        const messageRead = MessageReadRequest.create({
            chatId: chat_id,
            messageId: message_id,
        });
        // console.debug("Mark message", message_id, "as read in chat", chat_id);
        updateChat({type: "markAsRead", msg: messageRead});
        sendRequest(Request.create({
            messageRead
        }));
    }, [sendRequest, updateChat]);

    // Render the ChatContext.Provider component and pass the necessary values
    return (
        <ChatContext.Provider value={{
            chats,
            chatConnected: chatConnected && readyState === ReadyState.OPEN,
            chatBusy: busy,
            chatError: error,
            currentChat: !!chatID ? chats.get(chatID) : undefined,
            sendChatMessage,
            subscribeToChat,
            markMessageAsRead,
            unreadMessagesForChat,
            unreadChatMessages,
        }}>
            {children}
        </ChatContext.Provider>
    );
};